/**
 * Canonical anatomical region identifiers used by the 3D region-node overlay.
 * Positions are illustrative approximations for visualization, not derived from
 * an anatomical atlas — see regions.ts for the coordinates and a note on that.
 */
export type RegionId =
  // Central — brain
  | 'prefrontal_cortex'
  | 'amygdala'
  | 'hippocampus'
  | 'nucleus_accumbens'
  | 'vta'
  | 'raphe_nuclei'
  | 'locus_coeruleus'
  | 'thalamus'
  | 'visual_cortex'
  | 'motor_cortex'
  | 'striatum'
  | 'cerebellum'
  | 'hypothalamus'
  | 'brain_stem'
  // Central — spinal cord
  | 'spinal_dorsal_horn'
  | 'spinal_ventral_horn'
  // Peripheral and autonomic
  | 'autonomic_ganglia'
  | 'sympathetic_chain'
  | 'vagus_nerve'
  | 'peripheral_sensory'
  | 'neuromuscular_junction'
  | 'enteric_plexus';

/**
 * Subdivision of the nervous system a structure belongs to. Defined here rather
 * than alongside the geometry because it is a domain concept the pharmacology
 * layer reasons about — which parts of the nervous system a drug engages.
 */
export type NervousSystem = 'central' | 'somatic' | 'autonomic' | 'enteric';

/**
 * Where a structure sits.
 *
 * Brain structures are anchored in MNI space, the citable standard for
 * neuroimaging coordinates. MNI is a brain template and says nothing about the
 * spinal cord or periphery, so structures below the head are anchored in the
 * body frame instead: millimetres from the brain centroid, +x right, +y
 * superior, +z anterior.
 */
export type RegionAnchor =
  | { space: 'mni'; coords: [number, number, number] }
  | { space: 'body'; coords: [number, number, number] };

/** Pharmacological action a substance exerts at a given receptor. */
export type ReceptorAction =
  | 'agonist'
  | 'partial-agonist'
  | 'antagonist'
  | 'positive-modulator'
  | 'negative-modulator'
  | 'reuptake-inhibitor';

/**
 * A one-compartment oral pharmacokinetic model: absorption (ka) and
 * elimination (ke) first-order rate constants, volume of distribution (vd),
 * and oral bioavailability (F). Appropriate for exogenous substances that are
 * ingested and cleared over time.
 *
 * Plasma concentration is computed in mg/L, then converted to nanomolar via
 * molecularWeightGMol so it can be compared against receptor Ki values.
 * brainPlasmaRatio is a simplified single-number stand-in for blood-brain
 * barrier penetration (1.0 = brain concentration tracks plasma 1:1); real
 * CNS pharmacokinetics involve time-delayed equilibration this model does
 * not attempt to capture.
 */
export interface OralPKParams {
  route: 'oral';
  /** Absorption rate constant, 1/hour. */
  ka: number;
  /** Elimination rate constant, 1/hour. */
  ke: number;
  /** Volume of distribution, liters. */
  vd: number;
  /** Oral bioavailability, 0-1. */
  bioavailability: number;
  /** Molecular weight, g/mol — required to convert mg/L to nanomolar. */
  molecularWeightGMol: number;
  /** Simplified brain:plasma concentration ratio (see note above). */
  brainPlasmaRatio: number;
}

/**
 * A direct-release model for endogenous neurotransmitters/hormones, which are
 * not "dosed" orally — the slider instead represents a release/synthesis
 * level directly in nanomolar, and concentration decays via a single
 * clearance rate constant. Using an absorption-phase PK model for these
 * would misrepresent how they actually enter circulation, so they get their
 * own simpler model instead.
 */
export interface DirectReleasePKParams {
  route: 'direct-release';
  /** Elimination/clearance rate constant, 1/hour. */
  ke: number;
}

/**
 * A hybrid absorption / zero-order-elimination model for substances like
 * ethanol whose elimination proceeds at a roughly constant rate (mg/L per
 * hour) rather than a fixed half-life — forcing these into the exponential
 * OralPKParams model would misrepresent the real shape of the concentration
 * curve. The rising phase still uses a saturating-exponential absorption
 * term (a simplification: ethanol's absorption itself isn't strictly
 * first-order either, but this is a much smaller distortion than getting the
 * elimination phase's shape wrong).
 */
export interface ZeroOrderEliminationPKParams {
  route: 'zero-order-elimination';
  /** Absorption rate constant, 1/hour. */
  ka: number;
  /** Volume of distribution, liters. */
  vd: number;
  /** Oral bioavailability, 0-1. */
  bioavailability: number;
  /** Molecular weight, g/mol — required to convert mg/L to nanomolar. */
  molecularWeightGMol: number;
  /** Simplified brain:plasma concentration ratio. */
  brainPlasmaRatio: number;
  /** Constant elimination rate, mg/L per hour. */
  eliminationRateMgPerLPerHour: number;
}

export type PKParams = OralPKParams | DirectReleasePKParams | ZeroOrderEliminationPKParams;

export interface ReceptorBinding {
  /** Key into the RECEPTORS registry (receptors.ts). */
  receptor: string;
  /** Binding affinity, nanomolar. Lower = tighter binding. */
  kiNM: number;
  action: ReceptorAction;
  /**
   * Human-readable citation for this Ki value (author/year/journal, PMID/DOI,
   * or a named reference database such as the NIMH PDSP Ki Database). Never
   * leave this blank — if a value is a rough/approximate figure, say so
   * explicitly here rather than presenting it with false precision.
   */
  citation: string;
}

export interface ColorPalette {
  /** Primary hex color driving wireframe/particle tint at low-to-mid activity. */
  primary: string;
  /** Secondary hex color blended in at high activity/occupancy. */
  secondary: string;
  /** Multiplier applied to baseline particle speed (1.0 = no change). */
  particleSpeedMultiplier: number;
  /** When true, the visualization cycles hue over time instead of a fixed palette (e.g. classic psychedelics). */
  rainbowMode?: boolean;
}

/**
 * Defines the dose slider's range for a substance, in the same canonical
 * units the PK model consumes (mg for oral/zero-order-elimination routes,
 * nanomolar release level for direct-release routes) — real substances span
 * micrograms (LSD) to tens of grams (ethanol), so a single fixed-unit slider
 * range across all substances would be unusable for most of them.
 * unit/displayScale are purely for UI presentation, e.g. showing "14 g"
 * instead of "14000 mg" (displayScale 0.001, unit 'g').
 */
export interface DoseRange {
  min: number;
  max: number;
  default: number;
  step: number;
  unit: string;
  displayScale: number;
}

/** Grouping used to organise the substance browser. */
export type SubstanceClass =
  | 'stimulant'
  | 'depressant'
  | 'opioid'
  | 'psychedelic'
  | 'dissociative'
  | 'cannabinoid'
  | 'medicine'
  | 'endogenous';

export const SUBSTANCE_CLASS_LABELS: Record<SubstanceClass, string> = {
  stimulant: 'Stimulants',
  depressant: 'Depressants',
  opioid: 'Opioids',
  psychedelic: 'Psychedelics',
  dissociative: 'Dissociatives',
  cannabinoid: 'Cannabinoids',
  medicine: 'Medicines',
  endogenous: 'Endogenous',
};

export interface Substance {
  id: string;
  displayName: string;
  emoji: string;
  /** Lowercase aliases/common names matched by the fuzzy search. */
  aliases: string[];
  substanceClass: SubstanceClass;
  mechanismSummary: string;
  pk: PKParams;
  doseRange: DoseRange;
  bindings: ReceptorBinding[];
  colorPalette: ColorPalette;
}
